/**
 * Worker Withdrawal Contracts - Database-First Zod Schemas
 *
 * SOURCE OF TRUTH: PostgreSQL (MCP queries 2026-01-02)
 *
 * Table: withdrawals
 * Key functions: get_pending_withdrawals, assign_withdrawal
 * Key triggers: handle_withdrawal_log (status transitions)
 *
 * Withdrawal status flow:
 *   pending -> paid (money sent) -> completed (confirmed)
 *   pending -> declined (confiscated, NO REFUND)
 *
 * CHECK constraint 'valid_withdrawal_status':
 *   ('pending', 'paid', 'completed', 'declined')
 *
 * CHECK constraint 'valid_withdrawal_log_type':
 *   ('assignment', 'cascade', 'webhook', 'manual', 'edit', 'decline', 'complete', 'admin_edit', 'resend')
 */
import { z } from 'zod';
import { bigintString, timestampString, withdrawalStatusSchema, successResponseSchema, } from '../common.js';
// =============================================================================
// STORED PROCEDURE: get_pending_withdrawals
// Args: p_limit integer DEFAULT 50
// Returns: TABLE(reference_number, user_telegram_id, username, amount, card_number, assigned_to, created_at)
// =============================================================================
export const getPendingWithdrawalsInputSchema = z.object({
    limit: z.number().int().positive().default(50),
});
/**
 * Row from get_pending_withdrawals() - matches backend query exactly
 * Backend enriches with: first_name, original_bonus, status, lock info
 */
export const pendingWithdrawalRowSchema = z.object({
    reference_number: z.string(),
    user_telegram_id: bigintString,
    username: z.string().nullable(),
    amount: z.number().int(),
    card_number: z.string().nullable(),
    assigned_to: z.string().nullable(),
    created_at: timestampString,
});
// =============================================================================
// STORED PROCEDURE: assign_withdrawal
// Args: p_withdrawal_ref varchar, p_staff_id varchar
// Returns: TABLE(success boolean, message text)
// =============================================================================
export const assignWithdrawalInputSchema = z.object({
    withdrawal_ref: z.string(),
    staff_id: z.string(),
});
export const assignWithdrawalResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});
// =============================================================================
// STORED PROCEDURE: get_withdrawal_by_ref
// Args: p_reference_number varchar
// Returns: TABLE with 17 columns (see database)
// =============================================================================
export const getWithdrawalByRefResultSchema = z.object({
    reference_number: z.string(),
    user_telegram_id: bigintString,
    original_amount: z.number().int(),
    original_bonus: z.number().int().nullable(),
    amount: z.number().int(),
    status: withdrawalStatusSchema,
    completed_at: timestampString,
    assigned_to: z.string().nullable(),
    card_number: z.string().nullable(),
    provider_hash: z.string().nullable(),
    provider_transaction_id: z.string().nullable(),
    is_manual: z.boolean().nullable(),
    usdt_amount: z.string().nullable(), // numeric → string
    exchange_rate: z.string().nullable(),
    commission: z.string().nullable(),
    photo_url: z.string().nullable(),
    resend_used: z.boolean().nullable(),
    created_at: timestampString,
});
// =============================================================================
// API RESPONSES: GET /api/worker/withdrawals
// Backend controller: getPendingWithdrawals
// =============================================================================
/**
 * Individual withdrawal in pending list (worker view)
 * Backend maps DB row to camelCase + adds lock info
 */
export const workerWithdrawalSchema = z.object({
    // From database
    id: z.string(), // = reference_number
    referenceNumber: z.string(), // = reference_number (duplicate for clarity)
    telegramId: z.string(), // bigint → string
    username: z.string().nullable(),
    firstName: z.string().nullable(), // from users join
    amount: z.number().int(), // final payout amount
    originalAmount: z.number().int().optional(), // if different from amount
    bonusDeducted: z.number().int().nullable(), // = original_bonus
    cardNumber: z.string().nullable(),
    status: withdrawalStatusSchema,
    providerTransactionId: z.string().nullable(), // set when cascade started
    createdAt: z.string(), // ISO timestamp
    // From Redis lock system (not database)
    isLocked: z.boolean(),
    lockedBy: z.string().nullable(),
    // Legacy fields (kept for frontend compatibility, may be null)
    actualPayout: z.number().int().nullable(),
    editedAmount: z.number().int().nullable(),
});
/**
 * GET /api/worker/withdrawals response
 */
export const pendingWithdrawalsResponseDataSchema = z.object({
    withdrawals: z.array(workerWithdrawalSchema),
    total: z.number().int(),
});
export const pendingWithdrawalsResponseSchema = successResponseSchema(pendingWithdrawalsResponseDataSchema);
// =============================================================================
// API RESPONSES: GET /api/worker/withdrawals/:transactionId
// Backend controller: getWithdrawalDetails
// =============================================================================
export const withdrawalDetailTransactionSchema = z.object({
    id: z.string(),
    referenceNumber: z.string(),
    telegramId: z.string(),
    amount: z.number().int(),
    status: withdrawalStatusSchema,
    cardNumber: z.string().nullable(),
    createdAt: z.string(),
});
export const withdrawalDetailResponseDataSchema = z.object({
    transaction: withdrawalDetailTransactionSchema,
    isLocked: z.boolean(),
    lockedBy: z.string().nullable(),
});
export const withdrawalDetailResponseSchema = successResponseSchema(withdrawalDetailResponseDataSchema);
// =============================================================================
// API RESPONSES: POST /api/worker/withdrawals/:transactionId/lock
// Backend controller: lockWithdrawal
// =============================================================================
export const lockWithdrawalResponseDataSchema = z.object({
    message: z.string(),
    transactionId: z.string(),
    expiresIn: z.number().int(), // seconds (default 300)
});
export const lockWithdrawalResponseSchema = successResponseSchema(lockWithdrawalResponseDataSchema);
// =============================================================================
// API RESPONSES: POST /api/worker/withdrawals/:transactionId/unlock
// Backend controller: unlockWithdrawal
// =============================================================================
export const unlockWithdrawalResponseDataSchema = z.object({
    message: z.string(),
});
export const unlockWithdrawalResponseSchema = successResponseSchema(unlockWithdrawalResponseDataSchema);
// =============================================================================
// API RESPONSES: POST /api/worker/withdrawals/:transactionId/auto-cascade
// Backend controller: autoCascade
// =============================================================================
export const autoCascadeAttemptSchema = z.object({
    method: z.string(),
    result: z.enum(['success', 'failed', 'skipped']),
    error: z.string().optional(),
    durationMs: z.number().int().optional(),
});
export const autoCascadeResponseDataSchema = z.object({
    message: z.string(),
    transactionId: z.string(),
    payoutId: z.string().optional(),
    method: z.string().optional(),
    status: z.enum(['pending', 'processing', 'completed']),
    note: z.string().optional(),
    attempts: z.array(autoCascadeAttemptSchema).optional(),
});
export const autoCascadeResponseSchema = successResponseSchema(autoCascadeResponseDataSchema);
// =============================================================================
// API REQUESTS: POST /api/worker/withdrawals/:transactionId/manual
// Backend controller: manualComplete
// =============================================================================
export const manualCompleteRequestSchema = z.object({
    photoFileId: z.string().optional(),
    photoMessageId: z.number().int().optional(),
    amountSent: z.number().int().positive().optional(),
});
export const manualCompleteResponseDataSchema = z.object({
    message: z.string(),
    transactionId: z.string(),
    amountSent: z.number().int(),
});
export const manualCompleteResponseSchema = successResponseSchema(manualCompleteResponseDataSchema);
// =============================================================================
// API REQUESTS: POST /api/worker/withdrawals/:transactionId/decline
// Backend controller: declineWithdrawal
// =============================================================================
export const declineWithdrawalRequestSchema = z.object({
    reason: z.string().optional(), // Optional - controller uses default if not provided
});
export const declineWithdrawalResponseDataSchema = z.object({
    message: z.string(),
    transactionId: z.string(),
    confiscatedAmount: z.number().int(),
});
export const declineWithdrawalResponseSchema = successResponseSchema(declineWithdrawalResponseDataSchema);
// =============================================================================
// API REQUESTS: POST /api/worker/withdrawals/:transactionId/edit-amount
// Backend controller: editAmount
// =============================================================================
export const editAmountRequestSchema = z.object({
    newAmount: z.number().int().positive('New amount must be positive'),
});
export const editAmountResponseDataSchema = z.object({
    message: z.string(),
    transactionId: z.string(),
    originalAmount: z.number().int(),
    newAmount: z.number().int(),
    difference: z.number().int(),
});
export const editAmountResponseSchema = successResponseSchema(editAmountResponseDataSchema);
// =============================================================================
// API RESPONSES: POST /api/worker/withdrawals/:transactionId/confirm-complete
// Backend controller: confirmComplete
// =============================================================================
export const confirmCompleteResponseDataSchema = z.object({
    message: z.string(),
    transactionId: z.string(),
    amount: z.number().int(),
});
export const confirmCompleteResponseSchema = successResponseSchema(confirmCompleteResponseDataSchema);
//# sourceMappingURL=withdrawals.js.map